import React, { useEffect, useState } from 'react';
import { Form, Input, Modal, Select, Tabs } from 'antd';
import api from '../../../services/api';
import { ApiUser } from '../../../types';

const { Option } = Select;

interface ReassignLeadModalProps {
  visible: boolean;
  onCancel: () => void;
  lead: any;
  onAssignSubmit: (leadId: string, assignedToId: string) => void;
  onReassignSubmit: (leadId: string, assignedToId: string, reason: string) => void;
  message: any;
  hasPermission: (permission: string) => boolean;
}

export const ReassignLeadModal: React.FC<ReassignLeadModalProps> = ({
  visible,
  onCancel,
  lead,
  onAssignSubmit,
  onReassignSubmit,
  message,
  hasPermission,
}) => {
  const [form] = Form.useForm();
  const [users, setUsers] = useState<ApiUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('assign');

  const canAssign = hasPermission('lead.assign');
  const canReassign = hasPermission('lead.reassigned');

  const isInitialAssignment = !lead?.assigned_to;
  const title = `Leads: ${lead?.full_name || ''}`;

  useEffect(() => {
    if (visible && lead) {
      form.resetFields();
      setLoading(true);

      if (canAssign) {
        setActiveTab('assign');
      } else if (canReassign) {
        setActiveTab('reassign');
      }

      api.get(`leads/users`, { params: { property_area: lead.property_area?.id } })
        .then(response => {
          const allUsers: ApiUser[] = response.data.data || [];
          const filteredUsers = isInitialAssignment
            ? allUsers
            : allUsers.filter((user: ApiUser) => user.id !== lead.assigned_to?.id);
          setUsers(filteredUsers);
        })
        .catch(error => {
          console.error('Failed to fetch users:', error);
          message.error('Gagal memuat data pengguna untuk area properti ini.');
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [lead, visible, form, message, isInitialAssignment, canAssign, canReassign]);

  const handleOk = () => {
    form.validateFields()
      .then(values => {
        if (values.assigned_to_id === lead.assigned_to?.id) {
          message.info('Tidak ada perubahan pada penugasan.');
          onCancel();
          return;
        }

        if (activeTab === 'assign') {
          onAssignSubmit(lead.id, values.assigned_to_id);
        } else {
          onReassignSubmit(lead.id, values.assigned_to_id, values.reason);
        }

        form.resetFields();
      })
      .catch(info => {
        console.log('Validate Failed:', info);
      });
  };

  const renderForm = (isReassignTab: boolean) => (
    <Form form={form} layout="vertical">
      {isReassignTab && (
        <p>
          <strong>Saat ini ditugaskan ke:</strong> {lead?.assigned_to?.full_name || 'Tidak ada'}
        </p>
      )}
      <Form.Item
        name="assigned_to_id"
        label={isReassignTab ? 'Reassign kepada' : 'Assign kepada'}
        rules={[{ required: true, message: 'Silakan pilih pengguna!' }]}
      >
        <Select
          showSearch
          placeholder="Pilih sales agent"
          loading={loading}
          optionFilterProp="children"
          filterOption={(input, option: any) =>
            option.children.toLowerCase().includes(input.toLowerCase())
          }
        >
          {users.map(user => (
            <Option key={user.id} value={user.id}>{user.full_name}</Option>
          ))}
        </Select>
      </Form.Item>
      {isReassignTab && (
        <Form.Item
          name="reason"
          label="Alasan Reassign"
          rules={[{ required: true, message: 'Silakan masukkan alasan!' }]}
        >
          <Input.TextArea rows={4} placeholder="Jelaskan alasan pemindahan sales agent" />
        </Form.Item>
      )}
    </Form>
  );

  return (
    <Modal
      title={title}
      open={visible}
      onOk={handleOk}
      onCancel={onCancel}
      confirmLoading={loading}
      destroyOnClose
    >
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        {canAssign && (
          <Tabs.TabPane tab="Assign to Sales" key="assign">
            {renderForm(false)}
          </Tabs.TabPane>
        )}
        {canReassign && (
          <Tabs.TabPane tab="Reassign" key="reassign">
            {renderForm(true)}
          </Tabs.TabPane>
        )}
      </Tabs>
    </Modal>
  );
};

export default ReassignLeadModal;
